import { AbstractGameView } from "@lib/titan2-slingo/dist/game/view/abstract/display/AbstractGameView";
import { AppEvent } from "@lib/titan2/dist/game/Constants";
import { TitanPanel } from "@lib/titan2/dist/game/control/TitanPanel";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { LoaderEvent } from "@lib/titan2/dist/loader/event/LoaderEvent";
import { Delay } from "@lib/titan2/dist/util/time/Delay";
import { MouseUtil } from "@lib/titan2/dist/util/view/MouseUtil";
import { GameEvent } from "../../../controller/event/GameEvent";
/**
 * Binds game feature notes into a cycling notepad.
 */
export class GameFeatureNotepad extends AbstractGameView {
    create() {
        super.create();
        // Create a panel for arranging the notes.
        this.notesPanel = TitanPanel.createPanel(this, "notes-panel", this.landscapeProperties.notesPanelProperties, this.portraitProperties?.notesPanelProperties);
        this.createNotes();
        this.notesPanel.arrange();
        for (let i = 0; i < this.notesPanel.getTotalChildren(); ++i) {
            const note = this.notesPanel.getPanelChildAt(i);
            note.alpha = this.getNoteAlphaAt(i);
        }
        MouseUtil.addMouseUp(this.notesPanel, this.handleInteraction, this);
    }
    createNotes() {
        this.chanceGameNote = this.factory.createChanceGameNote();
        this.notesPanel.addToPanel(this.chanceGameNote, false);
        this.winLineNote = this.factory.createWinLineNote();
        this.notesPanel.addToPanel(this.winLineNote, false);
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(GameEvent.WIN_LINE_SUSPENSE, this.handleWinLineSuspense);
        this.registerGame(GameEvent.SCATTER_WIN_SUSPENSE, this.handleChanceGameSuspense);
        this.registerGame(LoaderEvent.LOADER_REMOVE, this.handleAutoCycle);
        this.registerGame(CommonGameEvent.PURCHASE_SPIN, this.handlePurchaseSpins);
        this.registerGame(CommonGameEvent.PURCHASE_ENTRY, this.handleNewSpin);
        this.registerCore(AppEvent.IDLE, this.handleIdle);
        this.registerCore(AppEvent.BUSY, this.handleBusy);
    }
    handlePurchaseSpins() {
        this.cancelCycleDelay();
    }
    handleNewSpin() {
        if (this.autoCycleDelay)
            return;
        this.nextCycleDelay(5000);
    }
    handleInteraction() {
        this.cycleNextNoteToFront();
        // Start the auto cycle delay after interaction if the game hasn't started yet.
        if (this.model.isPurchaseEntry()) {
            this.nextCycleDelay(15000);
        }
    }
    handleAutoCycle() {
        this.cycleNextNoteToFront();
        if (this.model.isPurchaseSpin())
            return;
        this.nextCycleDelay(5000);
    }
    handleWinLineSuspense() {
        // Bring the relevant win line information to the front.
        this.cycleNoteToFront(this.winLineNote);
    }
    handleChanceGameSuspense() {
        // Bring the relevant scatter win information to the front.
        this.cycleNoteToFront(this.chanceGameNote);
    }
    handleIdle() {
        // Interaction is enabled when the game is idle.
        MouseUtil.setInteractive(this.notesPanel, true);
    }
    handleBusy() {
        // Interaction is disabled when the game is in progress.
        MouseUtil.setInteractive(this.notesPanel, false);
        this.cancelCycleDelay();
    }
    handleRotate(event) {
        super.handleRotate(event);
        for (let i = 0; i < this.notesPanel.getTotalChildren(); ++i) {
            const note = this.notesPanel.getPanelChildAt(i);
            gsap.getTweensOf(note).forEach(t => t.progress(1)); // Complete the animation
        }
        this.notesPanel.arrange();
    }
    nextCycleDelay(delay) {
        // Not interacting with the panel will trigger the next note after a delay.
        this.autoCycleDelay?.dispose();
        this.autoCycleDelay = Delay.create(delay, this.handleAutoCycle, this);
    }
    cancelCycleDelay() {
        this.autoCycleDelay?.dispose();
        this.autoCycleDelay = undefined;
    }
    cycleNextNoteToFront() {
        // Bring the next note to the front.
        const nextNote = this.notesPanel.getPanelChildAt(this.notesPanel.getTotalChildren() - 2);
        this.cycleNoteToFront(nextNote);
    }
    cycleNoteToFront(nextNote) {
        if (this.notesPanel.getTotalChildren() <= 1) {
            return;
        }
        const topNote = this.notesPanel.getPanelChildAt(this.notesPanel.getTotalChildren() - 1);
        if (topNote === nextNote) {
            return;
        }
        // Cache the positions of the notes before rearrangement.
        const notePositions = new Map();
        for (let i = 0; i < this.notesPanel.getTotalChildren(); ++i) {
            const note = this.notesPanel.getPanelChildAt(i);
            notePositions.set(note, note.position.clone());
        }
        // Rearrange the current top note to the bottom of the panel and the next note to the top of the panel.
        this.notesPanel.removeFromPanel(topNote, false);
        this.notesPanel.removeFromPanel(nextNote, false);
        this.notesPanel.addToPanelAt(topNote, 0, false);
        this.notesPanel.addToPanel(nextNote, true);
        // Animate from the cached positions to the new positions.
        const timeline = gsap.timeline({
            onStart: () => this.interactiveChildren = false,
            onComplete: () => this.interactiveChildren = true,
        });
        for (let i = 0; i < this.notesPanel.getTotalChildren(); ++i) {
            const note = this.notesPanel.getPanelChildAt(i);
            const fromPosition = notePositions.get(note);
            const animation = gsap.fromTo(note, {
                x: fromPosition.x,
                y: fromPosition.y,
            }, {
                x: note.x,
                y: note.y,
                alpha: this.getNoteAlphaAt(i),
                duration: 0.2,
                ease: "power2.inOut",
            });
            timeline.add(animation, "<");
        }
    }
    getNoteAlphaAt(index) {
        const t = index / (this.notesPanel.getTotalChildren() - 1);
        return this.uiProperties.notesFadeAlpha + (1 - this.uiProperties.notesFadeAlpha) * t;
    }
}
//# sourceMappingURL=GameFeatureNotepad.js.map